import React from 'react';
import { CreditCard, MessageSquare, Wrench, Bell, CheckCircle2, ArrowRight, WifiOff, RefreshCw, Zap, ArrowRightLeft } from 'lucide-react';
import { Button } from '@shared/ui/button';
import { useNotifications } from '../NotificationContext';
import { Notification } from '../types';
import {
  getNotificationTitle,
  getNotificationDescription,
  getNotificationCategory,
  getNotificationSeverity,
  isNotificationUnread,
} from '../utils';

// Map category → icon config (fallback to Bell for unknown categories)
const CATEGORY_ICON_MAP: Record<string, { Icon: React.ElementType; iconColor: string; iconBg: string }> = {
  Service: { Icon: Wrench, iconColor: 'text-purple-600', iconBg: 'bg-purple-100' },
  Complaint: { Icon: MessageSquare, iconColor: 'text-orange-600', iconBg: 'bg-orange-100' },
  Payment: { Icon: CreditCard, iconColor: 'text-blue-600', iconBg: 'bg-blue-100' },
  Disconnection: { Icon: Zap, iconColor: 'text-red-600', iconBg: 'bg-red-100' },
  Reconnection: { Icon: RefreshCw, iconColor: 'text-green-600', iconBg: 'bg-green-100' },
  Transfer: { Icon: ArrowRightLeft, iconColor: 'text-indigo-600', iconBg: 'bg-indigo-100' },
};

const getIconConfig = (category: string) => {
  return CATEGORY_ICON_MAP[category] || { Icon: Bell, iconColor: 'text-gray-600', iconBg: 'bg-gray-100' };
};

const getSeverityBadge = (severity: string) => {
  switch (severity) {
    case 'critical':
    case 'error':
      return 'bg-red-50 text-red-600';
    case 'warning':
      return 'bg-orange-50 text-orange-600';
    default:
      return 'bg-blue-50 text-blue-600';
  }
};

const NotificationListContent = ({
  filter,
  notifications,
  markAsRead,
}: {
  filter: string;
  notifications: Notification[];
  markAsRead: (id: number | string) => void;
}) => {
  const filteredNotifications =
    filter === 'All'
      ? notifications
      : notifications.filter((n) => getNotificationCategory(n) === filter);

  return (
    <div className="space-y-4">
      {filteredNotifications.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
          <p className="text-gray-500">No notifications found.</p>
        </div>
      ) : (
        filteredNotifications.map((notification) => {
          const category = getNotificationCategory(notification);
          const { Icon, iconColor, iconBg } = getIconConfig(category);
          const unread = isNotificationUnread(notification);
          const severity = getNotificationSeverity(notification);
          const title = getNotificationTitle(notification);
          const description = getNotificationDescription(notification);

          return (
            <div
              key={notification.id}
              className={`bg-white rounded-2xl p-5 border shadow-sm flex gap-4 transition-all hover:border-gray-300 ${
                unread ? 'border-blue-200 bg-blue-50/30' : 'border-gray-200'
              }`}
            >
              {/* Icon */}
              <div
                className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${iconBg} ${iconColor}`}
              >
                <Icon className="w-6 h-6" />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-bold text-gray-900 text-base truncate">{title}</h3>
                  {unread && (
                    <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />
                  )}
                </div>
                <p className="text-gray-500 text-sm mb-3 line-clamp-2">{description}</p>
                <p className="text-gray-400 text-xs font-medium">{notification.run_time}</p>
              </div>

              {/* Right Actions */}
              <div className="flex flex-col items-end justify-between shrink-0 ml-4">
                <div className="flex items-center gap-3">
                  <div
                    className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider ${getSeverityBadge(severity)}`}
                  >
                    {category}
                  </div>

                  {unread ? (
                    <button
                      onClick={() => markAsRead(notification.id)}
                      className="text-blue-500 hover:text-blue-700 transition-colors p-1 rounded-full hover:bg-blue-50"
                      title="Mark as read"
                    >
                      <CheckCircle2 className="w-4 h-4" strokeWidth={3} />
                    </button>
                  ) : (
                    <div className="w-6 h-6" />
                  )}
                </div>

                {notification.metadata?.request_no && (
                  <span className="text-xs font-medium text-gray-400 mt-4">
                    {notification.metadata.request_no}
                  </span>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export const NotificationLanding = () => {
  const { notifications, unreadCount, isConnected, isConnecting, error, markAsRead, markAllRead, reconnect } =
    useNotifications();

  return (
    <div className="flex flex-col">
      {/* Container */}
      <div className="w-full mx-auto p-4">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
            <p className="text-gray-500 mt-1">
              {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Connection status */}
            {!isConnected && !isConnecting && (
              <Button
                variant="outline"
                size="sm"
                className="border-orange-300 text-orange-700 bg-orange-50 hover:bg-orange-100"
                onClick={reconnect}
              >
                <WifiOff className="w-4 h-4 mr-2" />
                Reconnect
              </Button>
            )}

            {isConnecting && (
              <span className="flex items-center text-sm text-gray-500">
                <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" />
                Connecting…
              </span>
            )}

            <Button
              variant="outline"
              className="border-gray-300 text-gray-700 bg-white shadow-sm hover:bg-gray-50"
              onClick={markAllRead}
              disabled={unreadCount === 0}
            >
              <CheckCircle2 className="w-4 h-4 mr-2" /> Mark All Read
            </Button>
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Notifications List */}
        <NotificationListContent
          filter="All"
          notifications={notifications}
          markAsRead={markAsRead}
        />
      </div>
    </div>
  );
};